import React from 'react';
import { Package, RefreshCw, RotateCcw, Trash2 } from 'lucide-react';

import { formatIST } from '@/lib/utils';
import { CardAction, EmptyState, PageHeader, Pager, PhonePage, ScreenLoader, StatusPill } from '@/components/phone/primitives';
import { Colors } from '@/components/phone/theme';

/**
 * The recycle bin, on the admin's phone: one card per deleted order or line
 * item — what it was, whose, how big, who deleted it and when — with restore
 * and delete-forever underneath. Orders and line items page separately on the
 * server, so each list keeps its own pager.
 */
export default function PhoneRecycleBin({
  rows, loading, ordersTotal, lineItemsTotal,
  ordersPage, ordersPageCount, onOrdersPage,
  lineItemsPage, lineItemsPageCount, onLineItemsPage,
  onRefresh, onRestore, onDeleteForever,
}) {
  return (
    <PhonePage testID="recycle-bin-page">
      <PageHeader
        title="Recycle Bin"
        subtitle={`${ordersTotal} order${ordersTotal === 1 ? '' : 's'} · ${lineItemsTotal} line item${lineItemsTotal === 1 ? '' : 's'}`}
        action={(
          <button
            type="button"
            onClick={onRefresh}
            className="flex h-11 shrink-0 items-center gap-1 px-2 text-[13px] font-semibold"
            style={{ color: Colors.accent.DEFAULT }}
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        )}
      />

      <div className="flex-1 px-5 pb-8">
        {loading ? (
          <ScreenLoader />
        ) : rows.length === 0 ? (
          <EmptyState icon={Package} message="Nothing deleted" hint="The bin is empty." />
        ) : (
          <ul data-testid="recycle-bin-list">
            {rows.map((r) => {
              const isOrder = r.__kind === 'order';
              return (
                <li
                  key={r.__rowId}
                  data-testid={`bin-card-${r.__rowId}`}
                  className="mt-3 rounded-[10px] border"
                  style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
                >
                  <div className="px-4 pt-3">
                    <StatusPill label={isOrder ? 'Order' : 'Line item'} tone={isOrder ? 'accent' : 'neutral'} />
                    <p className="mt-2 line-clamp-2 text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
                      {isOrder
                        ? (r.line_items?.[0]?.item_name || r.customer_name || r.order_id)
                        : (r.item_name || r.product_subtype || r.order_type || 'Item')}
                    </p>
                    <p className="mt-0.5 truncate text-[12px] font-medium" style={{ color: Colors.text.secondary }}>
                      {r.order_id || r.order_display_id}{r.customer_name ? ` · ${r.customer_name}` : ''}
                    </p>
                    <p className="mt-0.5 text-[12px]" style={{ color: Colors.text.secondary }}>
                      {isOrder ? `${r.line_items?.length || 0} items` : `Qty ${r.quantity?.toLocaleString() || '—'}`}
                    </p>
                    <p className="py-3 text-[11px]" style={{ color: Colors.text.tertiary }}>
                      Deleted by {r.deleted_by_name || '—'}
                      {r.deleted_at ? ` · ${formatIST(r.deleted_at, { compact: true })}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 border-t px-3 py-2" style={{ borderColor: Colors.border.subtle }}>
                    <CardAction icon={RotateCcw} label="Restore" tone="positive" onClick={() => onRestore(r)}
                      testID={`bin-restore-${r.__rowId}`} />
                    <CardAction icon={Trash2} label="Delete forever" tone="critical" onClick={() => onDeleteForever(r)}
                      className="ml-auto" testID={`bin-purge-${r.__rowId}`} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {ordersPageCount > 1 && (
          <div className="mt-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: Colors.text.tertiary }}>Orders</p>
            <Pager page={ordersPage} totalPages={ordersPageCount} onPage={onOrdersPage} testID="orders-pager" />
          </div>
        )}
        {lineItemsPageCount > 1 && (
          <div className="mt-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: Colors.text.tertiary }}>Line items</p>
            <Pager page={lineItemsPage} totalPages={lineItemsPageCount} onPage={onLineItemsPage} testID="line-items-pager" />
          </div>
        )}
      </div>
    </PhonePage>
  );
}
